import React, { useEffect, useMemo, useState } from 'react';
import Api from '../../../Api';
import sendRequest from '../../../helpers/sendRequest';
import SearchInput from '../../../components/SearchInput';
import MyCheckBox from '../../../components/MyCheckbox';
import DaysSection from '../../AddOrganization/components/DaysSection';
import MapView from '../../AddOrganization/components/MapView';
import CloseSmallIcon from '../../../assets/formIcons/closeSmall.svg';
import {
  ALL_CITIES_OPTION_ID,
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from '../../../helpers/allCitiesOfCountry';
import { emptySchedule, fetchCitiesOfCountryCached } from '../../../helpers/organizationsBulkAutofill';
import { FIELD_TYPES } from '../constants';
import TemplatePhotosPicker from './TemplatePhotosPicker';
import CarouselPhotosField from '../../../components/CarouselPhotosField';

function useCitiesOfCountry(countryId) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!countryId) {
      setCities([]);
      return undefined;
    }
    setLoading(true);
    fetchCitiesOfCountryCached(countryId)
      .then((list) => {
        if (!cancelled) setCities(list);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [countryId]);

  return { cities, loading };
}

function FieldSelect({
  value, onChange, options, placeholder = '— Не выбрано —', disabled,
}) {
  return (
    <select
      className="bulk_offers_select"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function CategoryEditor({ value, onChange, categories }) {
  const options = useMemo(() => (categories || []).map((category) => ({
    value: category._id,
    label: `${category.name}${category.is_online ? ' — онлайн' : ''}`,
  })), [categories]);

  return (
    <FieldSelect
      value={value?._id}
      options={options}
      onChange={(id) => onChange((categories || []).find((category) => category._id === id) || null)}
      placeholder="— Выберите категорию —"
    />
  );
}

function CheckboxListEditor({
  items, selectedIds, onToggle, emptyText, withFlags,
}) {
  if (!items?.length) {
    return <p className="bulk_offers_field_note">{emptyText}</p>;
  }

  return (
    <div className="bulk_offers_checkbox_list">
      {items.map((item) => (
        <MyCheckBox
          key={item._id}
          value={selectedIds.includes(item._id)}
          onClick={() => onToggle(item)}
          text={item.name}
          imgUrl={withFlags && item.flag_url ? Api.url + item.flag_url : undefined}
          wrapperClassName="bulk_offers_checkbox_item"
        />
      ))}
    </div>
  );
}

function CityEditor({
  value, onChange, countries, lockedCountryId,
}) {
  const [countryId, setCountryId] = useState(value?.country_id || lockedCountryId || '');
  const { cities, loading } = useCitiesOfCountry(countryId);

  const country = useMemo(
    () => (countries || []).find((item) => item._id === countryId) || null,
    [countries, countryId],
  );

  const cityOptions = useMemo(() => ([
    { value: ALL_CITIES_OPTION_ID, label: 'Все города страны' },
    ...cities.map((city) => ({ value: city._id, label: city.name })),
  ]), [cities]);

  const handleCityChange = (cityId) => {
    if (!cityId) {
      onChange(null);
      return;
    }
    if (cityId === ALL_CITIES_OPTION_ID) {
      onChange(buildAllCitiesSelection(country));
      return;
    }
    const city = cities.find((item) => item._id === cityId);
    onChange(city ? { ...city, is_in_all_cities_of_country: false } : null);
  };

  const selectedCityValue = isAllCitiesSelection(value) ? ALL_CITIES_OPTION_ID : value?._id;

  return (
    <div className="bulk_offers_field_rows">
      <FieldSelect
        value={countryId}
        options={(countries || []).map((item) => ({ value: item._id, label: item.name }))}
        onChange={(id) => {
          setCountryId(id);
          onChange(null);
        }}
        placeholder="— Выберите страну —"
        disabled={Boolean(lockedCountryId)}
      />
      <FieldSelect
        value={selectedCityValue}
        options={cityOptions}
        onChange={handleCityChange}
        placeholder={loading ? 'Загрузка городов…' : '— Выберите город —'}
        disabled={!countryId || loading}
      />
    </div>
  );
}

function AdditionalCitiesEditor({ value, onChange, countryId }) {
  const { cities, loading } = useCitiesOfCountry(countryId);
  const selected = value || [];
  const selectedIds = selected.map((city) => city._id);

  const handleToggle = (city) => {
    if (selectedIds.includes(city._id)) {
      onChange(selected.filter((item) => item._id !== city._id));
    } else {
      onChange([...selected, city]);
    }
  };

  return (
    <CheckboxListEditor
      items={cities}
      selectedIds={selectedIds}
      onToggle={handleToggle}
      emptyText={loading ? 'Загрузка городов…' : 'Сначала выберите основной город — дополнительные города берутся из его страны.'}
    />
  );
}

function AddressEditor({
  value, onChange, cityId,
}) {
  const [dropdownAddresses, setDropdownAddresses] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    if (!value?.length || !cityId) {
      setDropdownAddresses([]);
      return () => controller.abort({ response: { data: 'Aborted' } });
    }

    sendRequest({
      request: Api.getDropdownAddresses,
      payload: { params: { address: value, city_id: cityId }, signal: controller.signal },
      shouldLogError: false,
      setLoading: setDropdownLoading,
    }).then((data) => {
      setDropdownAddresses(data?.status === 'ok' ? data.addresses : []);
    });

    return () => controller.abort({ response: { data: 'Aborted' } });
  }, [value, cityId]);

  const onAddressPress = async (item) => {
    const data = await sendRequest({
      request: Api.getAddressCoordinates,
      payload: { params: { place_id: item.place_id } },
    });

    onChange(item.address_main, data?.coordinates ? { coordinates: data.coordinates } : null);
  };

  return (
    <SearchInput
      wrapperClassName="bulk_offers_input"
      withLeftIcon={false}
      placeholder="Адрес"
      value={value || ''}
      onChange={(e) => onChange(typeof e === 'string' ? e : e.target.value)}
      shouldValueConsiderAsFocus={false}
      withDropdown={Boolean(cityId)}
      dropdownItems={dropdownAddresses}
      dropdownLabelField="address_full"
      dropdownKeyField="place_id"
      onDropdownItemPress={onAddressPress}
      dropdownLoading={dropdownLoading}
    />
  );
}

function CoordinatesEditor({ value, onChange, city }) {
  const handlePart = (part) => (e) => {
    const nextValue = typeof e === 'string' ? e : e.target.value;
    onChange({
      lat: value?.lat || '',
      lon: value?.lon || '',
      is_special_point_on_map: Boolean(value?.is_special_point_on_map),
      [part]: nextValue,
    });
  };

  const onMapPress = async (cords) => {
    const data = await sendRequest({
      request: Api.getPlaceNameWithCoordinates,
      payload: { params: cords },
    });

    if (data?.place_name) {
      onChange(cords, { address: data.place_name });
    }
  };

  return (
    <div className="bulk_offers_field_rows">
      <div className="bulk_offers_field_inline">
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          withCloseIcon={false}
          placeholder="Широта (lat)"
          value={value?.lat || ''}
          onChange={handlePart('lat')}
          shouldValueConsiderAsFocus={false}
        />
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          withCloseIcon={false}
          placeholder="Долгота (lon)"
          value={value?.lon || ''}
          onChange={handlePart('lon')}
          shouldValueConsiderAsFocus={false}
        />
      </div>
      <MapView
        city={city}
        coordinates={value}
        setCoordinates={(coordinates) => onChange(coordinates)}
        onMapPress={onMapPress}
      />
    </div>
  );
}

function ScheduleEditor({ value, onChange }) {
  const schedule = useMemo(() => ({ ...emptySchedule(), ...(value || {}) }), [value]);

  return (
    <div className="bulk_offers_schedule">
      <DaysSection
        schedule={schedule}
        setSchedule={(updater) => onChange(typeof updater === 'function' ? updater(schedule) : updater)}
      />
    </div>
  );
}

function TagsEditor({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const tags = value || [];

  const addTag = (tag) => {
    const clean = String(tag || '').trim();
    if (!clean || tags.includes(clean)) return;
    onChange([...tags, clean]);
  };

  const handleKeyDown = (ev) => {
    if (['Tab', 'Enter', 'Space'].includes(ev.code)) {
      ev.preventDefault();
      addTag(inputValue);
      setInputValue('');
    }
  };

  return (
    <div className="bulk_offers_field_rows">
      <SearchInput
        wrapperClassName="bulk_offers_input bulk_offers_input_with_hint"
        withLeftIcon={false}
        placeholder="Новый тэг"
        labelText="Enter, Tab, Space"
        value={inputValue}
        onChange={(e) => setInputValue(typeof e === 'string' ? e : e.target.value)}
        onKeyDown={handleKeyDown}
        shouldValueConsiderAsFocus={false}
      />
      <div className="bulk_offers_tags">
        {tags.map((tag) => (
          <div
            key={tag}
            className="bulk_offers_tag"
            onClick={() => onChange(tags.filter((item) => item !== tag))}
          >
            <p className="bulk_offers_tag_text">{tag}</p>
            <img src={CloseSmallIcon} alt="X" className="bulk_offers_tag_icon" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Один редактор на все типы полей предложения: используется и в карточке,
 * и в панели «редактировать поле у выбранных».
 */
function OfferFieldEditor({
  field,
  value,
  onChange,
  draft = null,
  categories = [],
  countries = [],
  languages = [],
  config = {},
  categoryForSubCategories = null,
  countryIdForCities = '',
}) {
  if (!field) return null;

  const handleChange = (nextValue, extra = null) => onChange(nextValue, extra);

  switch (field.type) {
    case FIELD_TYPES.text:
    case FIELD_TYPES.contact:
      return (
        <SearchInput
          wrapperClassName={`bulk_offers_input${field.digitsOnly ? ' bulk_offers_input_with_hint' : ''}`}
          withLeftIcon={false}
          placeholder={field.label}
          value={value || ''}
          onChange={(e) => handleChange(typeof e === 'string' ? e : e.target.value)}
          shouldValueConsiderAsFocus={false}
          labelText={field.digitsOnly ? 'только цифры' : undefined}
        />
      );

    case FIELD_TYPES.textarea:
      return (
        <SearchInput
          wrapperClassName="bulk_offers_input bulk_offers_input_large"
          withLeftIcon={false}
          placeholder={field.label}
          value={value || ''}
          onChange={(e) => handleChange(typeof e === 'string' ? e : e.target.value)}
          isLarge
          shouldValueConsiderAsFocus={false}
        />
      );

    case FIELD_TYPES.category:
      return (
        <CategoryEditor
          value={value}
          onChange={handleChange}
          categories={categories}
        />
      );

    case FIELD_TYPES.subCategories: {
      const category = draft?.category || categoryForSubCategories;
      const selected = value || [];
      const selectedIds = selected.map((item) => item._id);
      return (
        <CheckboxListEditor
          items={category?.sub_categories || []}
          selectedIds={selectedIds}
          onToggle={(item) => {
            if (selectedIds.includes(item._id)) {
              handleChange(selected.filter((sub) => sub._id !== item._id));
            } else {
              handleChange([...selected, item]);
            }
          }}
          emptyText={category
            ? 'У выбранной категории нет подкатегорий.'
            : 'Подкатегории можно менять только внутри одной категории — у выбранных предложений категории разные.'}
        />
      );
    }

    case FIELD_TYPES.city:
      return (
        <CityEditor
          value={value}
          onChange={handleChange}
          countries={countries}
          lockedCountryId={config?.country?._id || ''}
        />
      );

    case FIELD_TYPES.additionalCities:
      return (
        <AdditionalCitiesEditor
          value={value}
          onChange={handleChange}
          countryId={draft?.city?.country_id || countryIdForCities}
        />
      );

    case FIELD_TYPES.address:
      return (
        <AddressEditor
          value={value}
          onChange={handleChange}
          cityId={draft?.city?._id || ''}
        />
      );

    case FIELD_TYPES.coordinates:
      return (
        <CoordinatesEditor
          value={value}
          onChange={handleChange}
          city={draft?.city || null}
        />
      );

    case FIELD_TYPES.schedule:
      return <ScheduleEditor value={value} onChange={handleChange} />;

    case FIELD_TYPES.isForAllLanguages:
      return (
        <MyCheckBox
          value={Boolean(value)}
          setValue={(next) => handleChange(typeof next === 'function' ? next(Boolean(value)) : next)}
          text="Карточка подходит для любых языков"
        />
      );

    case FIELD_TYPES.languages: {
      const selected = value || [];
      const selectedIds = selected.map((item) => item._id);
      return (
        <CheckboxListEditor
          items={languages}
          selectedIds={selectedIds}
          onToggle={(item) => {
            if (selectedIds.includes(item._id)) {
              handleChange(selected.filter((lang) => lang._id !== item._id));
            } else {
              handleChange([...selected, item]);
            }
          }}
          emptyText="Справочник языков не загружен."
          withFlags
        />
      );
    }

    case FIELD_TYPES.tags:
      return <TagsEditor value={value} onChange={handleChange} />;

    case FIELD_TYPES.templatePhotos:
      return (
        <TemplatePhotosPicker
          value={value || []}
          onChange={handleChange}
          categories={categories}
          initialCategoryId={draft?.category?._id || categoryForSubCategories?._id || config?.category?._id || ''}
          initialSubCategoryId={config?.subCategory?._id || ''}
        />
      );

    case FIELD_TYPES.photos:
      return (
        <CarouselPhotosField
          value={value || []}
          onChange={handleChange}
          max={15}
        />
      );

    default:
      return null;
  }
}

export default OfferFieldEditor;
